import {
  CLOSING_WITHDRAWAL_REASON,
  CashDrawerBreakdown,
  CashSession,
  CashSessionMethodAmounts,
  EndOrderSource,
  Payment,
  PaymentMethodKey,
  PaymentMethodType,
  PaymentType,
  PaymentsTotals,
} from '@pedix-workspace/utils';
import { roundToCents } from '@pedix-workspace/shared-promotions';

export type { CashDrawerBreakdown, PaymentsTotals };

export const roundCurrency = (value: number): number => roundToCents(value);

/** La propina entra al cajón pero NO es ingreso del comercio, así que no cuenta como venta. */
const SALE_PAYMENT_TYPES = ['sale', 'refund'] as const satisfies readonly PaymentType[];

export type SalePaymentType = (typeof SALE_PAYMENT_TYPES)[number];

export const isSalePayment = <T extends Pick<Payment, 'type'>>(
  payment: T,
): payment is T & { type: SalePaymentType } => {
  return (SALE_PAYMENT_TYPES as readonly PaymentType[]).includes(payment.type);
};

export const isTipPayment = <T extends Pick<Payment, 'type'>>(payment: T): boolean => {
  return payment.type === 'tip';
};

const isManualMovementPayment = <T extends Pick<Payment, 'type'>>(payment: T): boolean => {
  return !isSalePayment(payment) && !isTipPayment(payment);
};

export type PaymentMethodRef = Pick<Payment, 'methodType'> & {
  details?: Pick<Payment['details'], 'methodName'>;
};

export const getPaymentMethodKey = (payment: PaymentMethodRef): PaymentMethodKey => {
  if (payment.methodType !== PaymentMethodType.OTHER) {
    return payment.methodType;
  }
  return payment.details?.methodName || payment.methodType;
};

export const isBuiltInPaymentMethodKey = (key: PaymentMethodKey): key is PaymentMethodType => {
  return Object.values(PaymentMethodType).includes(key as PaymentMethodType);
};

/**
 * Entradas y salidas del turno. Los importes ya vienen con signo, así que la dirección se lee del
 * signo y no de una tabla `tipo → signo`: agregar un tipo de pago no obliga a tocar esto.
 */
export const getPaymentsTotals = (payments: Payment[]): PaymentsTotals => {
  let totalIn = 0;
  let totalOut = 0;

  for (const payment of payments) {
    if (payment.amount > 0) {
      totalIn = roundCurrency(totalIn + payment.amount);
    } else {
      totalOut = roundCurrency(totalOut - payment.amount);
    }
  }

  return { totalIn, totalOut, net: roundCurrency(totalIn - totalOut) };
};

export const getCashMovementsTotals = (payments: Payment[]): PaymentsTotals => {
  return getPaymentsTotals(payments.filter(isManualMovementPayment));
};

export const getNonCashMovementsTotals = (payments: Payment[]): PaymentsTotals => {
  return getPaymentsTotals(
    payments.filter(
      payment => isManualMovementPayment(payment) && payment.methodType !== PaymentMethodType.CASH,
    ),
  );
};

export const getTipsTotal = (payments: Pick<Payment, 'type' | 'amount'>[]): number => {
  return roundCurrency(
    payments.reduce(
      (total, payment) => (isTipPayment(payment) ? total + payment.amount : total),
      0,
    ),
  );
};

export const getTipsByMethod = (payments: Payment[]): CashSessionMethodAmounts => {
  const tipsByMethod: CashSessionMethodAmounts = {};

  for (const payment of payments) {
    if (!isTipPayment(payment)) {
      continue;
    }
    const methodKey = getPaymentMethodKey(payment);
    tipsByMethod[methodKey] = roundCurrency((tipsByMethod[methodKey] || 0) + payment.amount);
  }

  return tipsByMethod;
};

export const sumMethodAmounts = (amountsByMethod: CashSessionMethodAmounts | undefined): number => {
  return Object.values(amountsByMethod ?? {}).reduce<number>(
    (total, amount) => roundCurrency(total + (amount ?? 0)),
    0,
  );
};

export type CashPaymentsSummary = {
  sales: number;
  salesCount: number;
  tips: number;
  extraIn: number;
  out: number;
};

export const getCashPaymentsSummary = (payments: Payment[]): CashPaymentsSummary => {
  const salePayments = payments.filter(isSalePayment);
  const movements = getCashMovementsTotals(payments);

  return {
    sales: roundCurrency(salePayments.reduce((total, payment) => total + payment.amount, 0)),
    salesCount: salePayments.filter(payment => payment.type === 'sale').length,
    tips: getTipsTotal(payments),
    extraIn: movements.totalIn,
    out: movements.totalOut,
  };
};

export type CashSessionTotals = {
  /** Lo que debería haber por cada método al cerrar. */
  expectedByMethod: CashSessionMethodAmounts;
  /** Atajo del anterior: es lo único que un humano cuenta. */
  expectedCash: number;
  totalsBySource: Partial<Record<EndOrderSource, number>>;
  paymentsCount: number;
};

/**
 * El arqueo entero.
 *
 * `esperado[EFECTIVO] = openingCash + Σ pagos en efectivo`, `esperado[otro] = Σ pagos de ese medio`.
 * La asimetría no es un caso especial: sólo el efectivo tiene un saldo inicial físico en el cajón.
 * No existe un saldo inicial de MercadoPago — lo digital arranca en cero cada corte y se concilia
 * contra el proveedor.
 *
 * Y por eso el efectivo de apertura NO es un pago: entre el cierre de un turno y la apertura del
 * siguiente la plata no se movió, es la misma observada dos veces. Modelarla como pago la contaría
 * dos veces.
 *
 * No se leen pedidos: no hay método que inferir ni precedencia que resolver.
 */
export const buildCashSessionTotals = (
  session: Pick<CashSession, 'openingCash'>,
  payments: Payment[],
): CashSessionTotals => {
  const expectedByMethod: CashSessionMethodAmounts = {};
  const totalsBySource: Partial<Record<EndOrderSource, number>> = {};

  for (const payment of payments) {
    const methodKey = getPaymentMethodKey(payment);
    expectedByMethod[methodKey] = roundCurrency(
      (expectedByMethod[methodKey] || 0) + payment.amount,
    );

    const source = payment.details.source;
    if (source && isSalePayment(payment)) {
      totalsBySource[source] = roundCurrency((totalsBySource[source] || 0) + payment.amount);
    }
  }

  expectedByMethod[PaymentMethodType.CASH] = roundCurrency(
    (expectedByMethod[PaymentMethodType.CASH] || 0) + session.openingCash,
  );

  return {
    expectedByMethod,
    expectedCash: expectedByMethod[PaymentMethodType.CASH] || 0,
    totalsBySource,
    paymentsCount: payments.length,
  };
};

/** El cajero cuenta el efectivo; lo digital lo concilia. La diferencia es de una sola cosa. */
export const getCashLeftInDrawer = (
  session: Pick<CashSession, 'closing' | 'countedCash'>,
): number | null => {
  return session.closing?.cashLeftInDrawer ?? session.countedCash ?? null;
};

export const getClosingTotalSold = (session: Pick<CashSession, 'closing'>): number => {
  return session.closing?.totalSold ?? 0;
};

export const getCashWithdrawnAtClose = (
  session: Pick<CashSession, 'closing' | 'countedCash'>,
): number | null => {
  const cashLeftInDrawer = getCashLeftInDrawer(session);

  if (session.countedCash === undefined || cashLeftInDrawer === null) {
    return null;
  }
  return roundCurrency(session.countedCash - cashLeftInDrawer);
};

export type ClosingWithdrawalPayment = Omit<Payment, 'id' | 'at'>;

export const buildClosingWithdrawalPayment = (
  session: Pick<CashSession, 'id' | 'establishmentId' | 'registerId'>,
  amount: number,
  createdBy: string,
): ClosingWithdrawalPayment => ({
  establishmentId: session.establishmentId,
  registerId: session.registerId,
  cashSessionId: session.id,
  type: 'withdrawal',
  methodType: PaymentMethodType.CASH,
  amount: -Math.abs(amount),
  details: {
    createdBy,
    origin: 'manual',
    reasonCode: CLOSING_WITHDRAWAL_REASON,
  },
});

export const getCashSessionDifference = (countedCash: number, expectedCash: number): number => {
  return roundCurrency(countedCash - expectedCash);
};

/** Lo vendido en el turno: sólo los cobros de pedidos, netos de devoluciones. La propina no. */
export const getCashSessionTotalSold = (payments: Pick<Payment, 'type' | 'amount'>[]): number => {
  return roundCurrency(
    payments.reduce((total, payment) => {
      return isSalePayment(payment) ? total + payment.amount : total;
    }, 0),
  );
};

/**
 * Lo vendido por método, para el desglose de la pantalla. Es distinto de `expectedByMethod`: no
 * incluye el efectivo de apertura, ni los retiros y gastos, ni la propina — porque nada de eso
 * es venta.
 */
export const getCashSalesByMethod = (payments: Payment[]): CashSessionMethodAmounts => {
  const salesByMethod: CashSessionMethodAmounts = {};

  for (const payment of payments) {
    if (!isSalePayment(payment)) {
      continue;
    }
    const methodKey = getPaymentMethodKey(payment);
    salesByMethod[methodKey] = roundCurrency((salesByMethod[methodKey] || 0) + payment.amount);
  }

  return salesByMethod;
};

export const getManualMovementsByMethod = (payments: Payment[]): CashSessionMethodAmounts => {
  const movementsByMethod: CashSessionMethodAmounts = {};

  for (const payment of payments) {
    if (payment.methodType === PaymentMethodType.CASH || !isManualMovementPayment(payment)) {
      continue;
    }
    const methodKey = getPaymentMethodKey(payment);
    movementsByMethod[methodKey] = roundCurrency(
      (movementsByMethod[methodKey] || 0) + payment.amount,
    );
  }

  return movementsByMethod;
};

export const buildOpeningAdjustmentPaymentId = (sessionId: string): string => {
  return `${sessionId}-opening`;
};

/**
 * De dónde sale el efectivo esperado. Los términos son disjuntos y suman exactamente
 * `expectedCash` — es lo que hace que el panel se pueda auditar a ojo. Ojo con reemplazarlos por
 * `getPaymentsTotals`: ése agrega TODOS los métodos y cuenta las ventas aparte, así que sumaría
 * dos veces lo cobrado en efectivo y metería lo digital en un desglose del cajón.
 *
 * El ajuste de apertura sale de `otherIn`/`otherOut` y va a su propio término: esa plata se movió
 * con la caja cerrada, y mezclarla con los movimientos del turno la disfraza de retiro del turno.
 */
export const getCashDrawerBreakdown = (
  session: Pick<CashSession, 'id' | 'openingCash'>,
  payments: Payment[],
): CashDrawerBreakdown => {
  const openingAdjustmentId = buildOpeningAdjustmentPaymentId(session.id);
  let openingAdjustment = 0;
  let sales = 0;
  let tips = 0;
  let otherIn = 0;
  let otherOut = 0;

  for (const payment of payments) {
    if (payment.methodType !== PaymentMethodType.CASH) {
      continue;
    }
    if (payment.id === openingAdjustmentId) {
      openingAdjustment = roundCurrency(openingAdjustment + payment.amount);
    } else if (isSalePayment(payment)) {
      sales = roundCurrency(sales + payment.amount);
    } else if (isTipPayment(payment)) {
      tips = roundCurrency(tips + payment.amount);
    } else if (payment.amount > 0) {
      otherIn = roundCurrency(otherIn + payment.amount);
    } else {
      otherOut = roundCurrency(otherOut - payment.amount);
    }
  }

  return { openingCash: session.openingCash, openingAdjustment, sales, tips, otherIn, otherOut };
};

export type CashSessionLiveTotals = CashSessionTotals & {
  totalSold: number;
  salesByMethod: CashSessionMethodAmounts;
  tipsByMethod: CashSessionMethodAmounts;
  movements: PaymentsTotals;
  cashDrawer: CashDrawerBreakdown;
};

export const buildCashSessionLiveTotals = (
  session: Pick<CashSession, 'id' | 'openingCash'>,
  payments: Payment[],
): CashSessionLiveTotals => {
  return {
    ...buildCashSessionTotals(session, payments),
    totalSold: getCashSessionTotalSold(payments),
    salesByMethod: getCashSalesByMethod(payments),
    tipsByMethod: getTipsByMethod(payments),
    movements: getCashMovementsTotals(payments),
    cashDrawer: getCashDrawerBreakdown(session, payments),
  };
};

export type CashSessionTotalsDelta = {
  paymentsCount: number;
  totalSold: number;
  byMethod: CashSessionMethodAmounts;
};

const applyDelta = (
  deltas: Map<string, CashSessionTotalsDelta>,
  payment: Payment | undefined,
  sign: 1 | -1,
): void => {
  if (!payment?.cashSessionId) {
    return;
  }

  const delta = deltas.get(payment.cashSessionId) ?? {
    paymentsCount: 0,
    totalSold: 0,
    byMethod: {},
  };

  delta.paymentsCount += sign;
  delta.totalSold = roundCurrency(
    delta.totalSold + (isSalePayment(payment) ? sign * payment.amount : 0),
  );
  const methodKey = getPaymentMethodKey(payment);
  delta.byMethod[methodKey] = roundCurrency(
    (delta.byMethod[methodKey] ?? 0) + sign * payment.amount,
  );

  deltas.set(payment.cashSessionId, delta);
};

export const buildCashSessionTotalsDeltas = (
  before: Payment | undefined,
  after: Payment | undefined,
): Map<string, CashSessionTotalsDelta> => {
  const deltas = new Map<string, CashSessionTotalsDelta>();

  applyDelta(deltas, before, -1);
  applyDelta(deltas, after, 1);

  for (const [sessionId, delta] of deltas) {
    const hasChange =
      delta.paymentsCount !== 0 ||
      delta.totalSold !== 0 ||
      Object.values(delta.byMethod).some(amount => amount !== 0);

    if (!hasChange) {
      deltas.delete(sessionId);
    }
  }

  return deltas;
};

export const getCashSessionLiveExpectedCash = (
  session: Pick<CashSession, 'openingCash' | 'liveTotals'>,
): number | null => {
  if (!session.liveTotals) {
    return null;
  }

  return roundCurrency(
    session.openingCash + (session.liveTotals.byMethod?.[PaymentMethodType.CASH] ?? 0),
  );
};
